import { useEffect, useRef, useState, memo, useCallback } from 'react';
import { Modal } from 'antd';
import Header from './header';
import BuyRecords from './buy-records';
import RightsList from './rights-list';
import PlaceOrder from './place-order';
import { getPayedList, getProjectCanPayList } from '@/apis';
import { CanPayItem, UserRights } from '@/types';
import queryDomMethods from '@/utils/query-dom-methods';
import { getGlobalStore } from '@/store/global';
import { recoverBodyScroll, stopBodyScroll } from '@/utils';
import './index.scss';

const RightsModal = () => {
    const userRightsInitInfo = useRef({
        lucaAskNum: 0,
        teacherAskNum: 0,
        subjects: [],
        orderRecords: [],
        aiCorrectAuth: false,
        aiCorrectNum: 0,
        aiCorrectSubjects: [],
        manualCorrectAuth: false,
        manualCorrectNum: 0,
        manualCorrectSubjects: [],
    });
    const [ininInfo, setInitInfo] = useState<{ lucaId: number; projectId: number }>();
    const [projectName, setProjectName] = useState('');
    const [userRights, setUserRights] = useState<UserRights>(userRightsInitInfo.current);
    const [noOrders, setNoOrders] = useState(false);
    const [userRightsNoData, setUserRightsNoData] = useState(false);
    const [categoryTitles, setCategoryTitles] = useState<string[]>([]);
    const [canPayList, setCanPayList] = useState<Array<CanPayItem>>([]);
    const [productInfo, setProductInfo] = useState<CanPayItem>();

    const visible = !!ininInfo?.lucaId;

    const initPayedList = useCallback(async () => {
        const { result } = await getPayedList({ projectId: ininInfo!.projectId });
        result &&
            setUserRights({
                ...result,
                orderRecords: result.orderRecords.map(item => {
                    const newItem = { ...item };
                    if (!result.aiCorrectAuth) {
                        newItem.aiCorrectNum = undefined;
                        newItem.aiCorrectNumStr = undefined;
                    }
                    if (!result.manualCorrectAuth) {
                        newItem.manualCorrectNum = undefined;
                        newItem.manualCorrectNumStr = undefined;
                    }
                    return newItem;
                }),
            });
        setNoOrders(!result?.orderRecords?.length);
    }, [ininInfo]);

    const initCanPayList = async () => {
        const { result } = await getProjectCanPayList({ lucaId: ininInfo!.lucaId, projectId: ininInfo!.projectId });
        if (!result) {
            setUserRightsNoData(true);
            return;
        }
        const { equity, projectName, aiCorrectAuth, manualCorrectAuth } = result;
        const titles = ['权益', 'Luca答疑', '名师答疑'];
        if (aiCorrectAuth) titles.push('智能批改');
        if (manualCorrectAuth) titles.push('人工批改');
        titles.push('有效期');
        const newEquity = (equity || []).map(item => {
            const newItem = { ...item };
            if (!aiCorrectAuth) {
                newItem.aiCorrectNum = undefined;
                newItem.aiCorrectNumStr = undefined;
                newItem.aiCorrectSubjects = undefined;
            }
            if (!manualCorrectAuth) {
                newItem.manualCorrectNum = undefined;
                newItem.manualCorrectNumStr = undefined;
                newItem.manualCorrectSubjects = undefined;
            }
            return newItem;
        });
        setCategoryTitles(titles);
        setProjectName(projectName);
        setCanPayList(newEquity);
        newEquity.length > 0 ? setProductInfo(newEquity[0]) : setUserRightsNoData(true);
    };

    const handleProductChange = useCallback((data: CanPayItem) => {
        setProductInfo(data);
    }, []);

    const handleClose = useCallback(() => setInitInfo(undefined), []);

    useEffect(() => {
        if (visible) {
            initPayedList();
            initCanPayList();
            stopBodyScroll();
        } else {
            setUserRights(userRightsInitInfo.current);
            setProjectName('');
            setCanPayList([]);
            setCategoryTitles([]);
            setProductInfo(undefined);
            setUserRightsNoData(false);
            setNoOrders(false);
            recoverBodyScroll();
        }
    }, [visible]);

    useEffect(() => {
        getGlobalStore()?.subscribe('RightsModal', ({ type, params }) => {
            switch (type) {
                case 'open':
                    setInitInfo(params);
                    break;
                default:
                    setInitInfo(undefined);
                    break;
            }
        });
    }, []);

    return (
        <Modal
            centered
            width={860}
            className="luca-rights-modals"
            open={visible}
            title=""
            closable={false}
            footer={null}
            getContainer={queryDomMethods.queryLucaQaRoot}
            zIndex={1900}
        >
            <Header userRights={userRights} projectName={projectName} onClose={handleClose} />
            <div className="luca-pay-box">
                <BuyRecords orderRecords={userRights.orderRecords} noData={noOrders} initPayedList={initPayedList} />
                <div className="rights-pay-box-container">
                    <RightsList
                        canPayList={canPayList}
                        categoryTitles={categoryTitles}
                        productId={productInfo?.productId}
                        noData={userRightsNoData}
                        onProductChange={handleProductChange}
                    />
                    {!!canPayList.length && (
                        <PlaceOrder ininInfo={ininInfo} productId={productInfo?.productId} productAmount={productInfo?.amount} />
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default memo(RightsModal);
